/* eslint-disable prettier/prettier */
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card } from 'antd';
import Illustration from '../assets/man-working.png';
import { CheckCircle, XCircle } from 'lucide-react';
import { UserService } from '../services/user.service';
import { NotFoundPage } from './NotFoundPage';
import { Button } from '../components/ui/Button';
import { useAuth } from '../context/AuthContext';
import { Input } from '../components/ui/Input';
import { CubeBackground } from '../components/shared/CubeBackground';

export const ResetPasswordPage = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { login } = useAuth();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errors, setErrors] = useState<any>({});
  const [tokenStatus, setTokenStatus] = useState<'valid' | 'invalid' | 'checking'>('checking');
  const [email, setEmail] = useState('');

  useEffect(() => {
    const verifyToken = async () => {
      try {
        const { data } = await UserService.verifyResetToken(token!);
        setEmail(data.email);
        setTokenStatus('valid');
      } catch (err: any) {
        setTokenStatus('invalid');
      }
    };
    if (token) verifyToken();
  }, [token]);

  const passwordRules = [
    { label: 'At least 6 characters', valid: password.length >= 6 },
    { label: 'Contains a capital letter', valid: /[A-Z]/.test(password) },
    { label: 'Contains a number', valid: /[0-9]/.test(password) },
  ];

  const validate = () => {
    const newErrors: any = {};
    if (!password) newErrors.password = 'Password is required';
    else {
      if (password.length < 6) newErrors.password = 'Password must be at least 6 characters';
      else if (!/[A-Z]/.test(password))
        newErrors.password = 'Password must contain at least one capital letter';
      else if (!/[0-9]/.test(password))
        newErrors.password = 'Password must contain at least one number';
    }

    if (confirmPassword !== password) newErrors.confirmPassword = 'Passwords do not match';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    try {
      await UserService.resetPassword({ token: token!, password });
      await login(email, password);
      navigate('/');
    } catch (err: any) {
      const raw = err?.response?.data;

      const message =
        typeof raw === 'string'
          ? raw
          : typeof raw?.message === 'string'
            ? raw.message
            : typeof raw?.message?.message === 'string'
              ? raw.message.message
              : 'Something went wrong. Please try again.';


      if (message.toLowerCase().includes('password')) {
        setErrors({ password: message });
      } else {
        setErrors({ token: message });
      }
    }
  };

  if (tokenStatus === 'checking') return null;

  if (tokenStatus === 'invalid') {
    return (
      <NotFoundPage
        message="This password reset link is invalid or has expired."
        buttonLabel="Go to login"
        buttonAction={() => navigate('/login')}
      />
    );
  }

  return (
    <div className="relative flex items-center justify-center min-h-screen overflow-hidden bg-light">
      <CubeBackground />

      <Card className="w-full max-w-md py-5 px-10 flex flex-col items-center shadow-lg bg-white relative z-10 min-h-[450px]">
        <img src={Illustration} alt="Illustration" className="w-32 h-32 mx-auto mb-6" />
        <h2 className="mb-6 text-2xl font-bold text-center text-primary">Reset Your Password</h2>

        {typeof errors.token === 'string' && (
          <p className="mb-4 text-center text-red-500">{errors.token}</p>
        )}

        <form className="w-full space-y-6 min-w-[360px]" onSubmit={handleSubmit}>
          <div>
            <Input
              className="w-full"
              placeholder="New Password"
              type="password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setErrors((prev: any) => ({ ...prev, password: '' }));
              }}
              error={!!errors.password}
            />
            {errors.password && <p className="text-sm text-red-500">{errors.password}</p>}
            {password !== '' && (
              <ul className="mt-2 space-y-1 text-sm">
                {passwordRules.map((rule, idx) => (
                  <li
                    key={idx}
                    className={`flex items-center gap-2 ${
                      rule.valid ? 'text-green-600' : 'text-red-500'
                    }`}
                  >
                    {rule.valid ? (
                      <CheckCircle className="w-4 h-4" />
                    ) : (
                      <XCircle className="w-4 h-4" />
                    )}
                    {rule.label}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div>
            <Input
              className="w-full"
              placeholder="Confirm Password"
              type="password"
              value={confirmPassword}
              onChange={(e) => {
                setConfirmPassword(e.target.value);
                setErrors((prev: any) => ({ ...prev, confirmPassword: '' }));
              }}
              error={!!errors.confirmPassword}
            />
            {errors.confirmPassword && (
              <p className="text-sm text-red-500">{errors.confirmPassword}</p>
            )}
          </div>

          <div>
            <Button type="submit" className="w-full">
              Reset Password
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
};
